import { useState, useRef } from 'react';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { useSpinner } from '@chikara/ui';

export function useUserLocation() {
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const { show, hide } = useSpinner();
  const isRequesting = useRef(false);

  const requestLocationPermission = async (isRetry = false) => {
    isRequesting.current = true;
    setErrorCode(null);
    setLocationError(false);
    show();

    if (!navigator.geolocation) {
      setLocationError(true);
      hide();
      isRequesting.current = false;
      return;
    }
  
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        tokenStorage.setLocation({ lat: latitude, lng: longitude });
  
        setLocationAllowed(true);
        setShowLocationModal(false);
        setLocationError(false);
        setErrorCode(null);
        hide();
        isRequesting.current = false;
      },
      error => {
        console.error('Geolocation error:', error);
        
        // Code 2: POSITION_UNAVAILABLE, Code 3: TIMEOUT
        if ((error.code === 2 || error.code === 3) && !isRetry) {
          console.log('Retrying with lower accuracy...');
          navigator.geolocation.getCurrentPosition(
            pos => {
              const { latitude, longitude } = pos.coords;
              tokenStorage.setLocation({ lat: latitude, lng: longitude });
              setLocationAllowed(true);
              setShowLocationModal(false);
              setLocationError(false);
              setErrorCode(null);
              hide();
              isRequesting.current = false;
            },
            err => {
              console.error('Retry failed:', err);
              handleFinalError(err);
            },
            { ...options, enableHighAccuracy: false }
          );
          return;
        }

        handleFinalError(error);
      },
      options
    );
  };

  const handleFinalError = (error: GeolocationPositionError) => {
    setErrorCode(error.code);
    setLocationError(true);
    setLocationAllowed(false);
    hide();
    isRequesting.current = false;
  };

  const openRequest = async () => {
    if (!navigator.geolocation) {
      setLocationError(true);
      setShowLocationModal(true);
      return;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      
      if (permission.state === 'granted') {
        requestLocationPermission();
      } else {
        setShowLocationModal(true);
        setLocationError(false);
        setErrorCode(null);
      }
    } catch (error) {
      setShowLocationModal(true);
      setLocationError(false);
      setErrorCode(null);
    }
  };

  const closeRequest = () => {
    setShowLocationModal(false);
    setErrorCode(null);
  };
      
  return { 
    locationAllowed, 
    showLocationModal, 
    locationError, 
    errorCode, 
    closeRequest, 
    openRequest, 
    requestLocationPermission 
  };
}